import { useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '@/stores/auth';
import { resolveWebSocketBaseUrl } from '@/utils/resolveApiBaseUrl';
import {
  useNotificationStore,
  type CreateNotificationData,
  type NotificationCategory,
  type NotificationType,
} from '@/stores/notification';

interface WebSocketMessage {
  type: string;
  id?: number;
  category?: string;
  title?: string;
  subtitle?: string;
  code?: string;
  department?: string;
  professionalName?: string;
  date?: string;
  timeRange?: string;
  user_id?: string;
  action?: string;
  establishmentConfigId?: number;
  schedulePlanningProfessionalIds?: number[];
}

interface UseWebSocketOptions {
  onConnected?: () => void;
  onDisconnected?: () => void;
  onError?: (error: Event) => void;
}

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000];
const HEARTBEAT_INTERVAL = 30000; // 30 seconds
export const RECORDS_UPDATED_EVENT = 'geoescala:records-updated';

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isConnectingRef = useRef(false);
  const isMountedRef = useRef(true);

  const token = useAuthStore((state) => state.token);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const addNotification = useNotificationStore(
    (state) => state.addNotification,
  );

  const clearHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const startHeartbeat = useCallback(() => {
    clearHeartbeat();
    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, HEARTBEAT_INTERVAL);
  }, [clearHeartbeat]);

  const processMessage = useCallback(
    (data: WebSocketMessage) => {
      if (data.type === 'connected') {
        options.onConnected?.();
        return;
      }

      if (data.type === 'pong') {
        return;
      }

      if (data.type === 'records.updated') {
        window.dispatchEvent(
          new CustomEvent<WebSocketMessage>(RECORDS_UPDATED_EVENT, {
            detail: data,
          }),
        );
        return;
      }

      if (data.title && data.category) {
        const notificationData: CreateNotificationData = {
          type: (data.type as NotificationType) || 'info',
          category: (data.category as NotificationCategory) || 'system',
          title: data.title,
          subtitle: data.subtitle,
          code: data.code,
          department: data.department,
          professionalName: data.professionalName,
          date: data.date,
          timeRange: data.timeRange,
          backendId: data.id,
        };

        addNotification(notificationData);
      }
    },
    [addNotification, options],
  );

  const connect = useCallback(() => {
    if (!token || !isAuthenticated || isConnectingRef.current) {
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    isConnectingRef.current = true;

    const wsUrl = `${resolveWebSocketBaseUrl()}/api/notification/ws?token=${token}`;

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        if (!isMountedRef.current) return;
        isConnectingRef.current = false;
        reconnectAttemptRef.current = 0;
        startHeartbeat();
      };

      wsRef.current.onmessage = (event) => {
        if (!isMountedRef.current) return;
        try {
          const data = JSON.parse(event.data) as WebSocketMessage;
          processMessage(data);
        } catch (error) {
          console.error('[WebSocket] Failed to parse message:', error);
        }
      };

      wsRef.current.onerror = (error) => {
        if (!isMountedRef.current) return;
        isConnectingRef.current = false;
        options.onError?.(error);
      };

      wsRef.current.onclose = (event) => {
        if (!isMountedRef.current) return;
        isConnectingRef.current = false;
        clearHeartbeat();
        options.onDisconnected?.();

        if (isAuthenticated && token) {
          const delay =
            RECONNECT_DELAYS[
              Math.min(reconnectAttemptRef.current, RECONNECT_DELAYS.length - 1)
            ];

          reconnectTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              reconnectAttemptRef.current++;
              connect();
            }
          }, delay);
        }
      };
    } catch (error) {
      isConnectingRef.current = false;
    }
  }, [
    token,
    isAuthenticated,
    startHeartbeat,
    clearHeartbeat,
    processMessage,
    options,
  ]);

  const disconnect = useCallback(() => {
    clearReconnectTimeout();
    clearHeartbeat();

    if (wsRef.current) {
      wsRef.current.close(1000, 'Client disconnecting');
      wsRef.current = null;
    }
  }, [clearReconnectTimeout, clearHeartbeat]);

  useEffect(() => {
    if (isAuthenticated && token) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      isMountedRef.current = false;
      disconnect();
    };
  }, [isAuthenticated, token]);

  return {
    isConnected: wsRef.current?.readyState === WebSocket.OPEN,
    connect,
    disconnect,
  };
}

export default useWebSocket;
